import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Wifi, Camera, Lightbulb, Volume2, Monitor, Settings, Phone, Mail, ArrowRight, CheckCircle, Star, Shield, Satellite, Send, Users, Bell, Info, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

const Index = () => {
  const [showImportantInfo, setShowImportantInfo] = useState(true);
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const { toast } = useToast();
  
  const handleNewsletterSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: "Email invalide",
        description: "Veuillez saisir une adresse email valide",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubscribing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('newsletter_subscribe_20251219_1730', {
        body: { email }
      });
      
      if (error) throw error;
      
      toast({
        title: "Inscription réussie !",
        description: data.message || "Merci de vous être abonné(e) à notre newsletter",
      });
      
      setEmail('');
    } catch (error: any) {
      toast({
        title: "Erreur d'inscription",
        description: error.message || "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubscribing(false);
    }
  };
  
  const services = [{
    icon: Volume2,
    title: "Audiovisuel & Multimédia",
    description: "Solutions complètes pour vos événements et présentations professionnelles",
    features: ["Systèmes de sonorisation", "Écrans et projecteurs", "Régie audiovisuelle"],
    image: "./images/tempImageCUEqKa.jpg"
  }, {
    icon: Monitor,
    title: "Sonorisation Professionnelle",
    description: "Équipements audio professionnels pour vos réunions et conférences",
    features: ["Micros sans fil", "Systèmes de diffusion", "Contrôle centralisé"],
    image: "./images/c47e6a2d-3edc-48d8-8bc0-826002b5fb22.jpg"
  }, {
    icon: Settings,
    title: "Domotique",
    description: "Automatisation intelligente pour votre confort et sécurité",
    features: ["Contrôle éclairage", "Gestion climatisation", "Sécurité intégrée"],
    image: "./images/tempImagescOfvq.jpg"
  }, {
    icon: Lightbulb,
    title: "Éclairage Scénique",
    description: "Solutions d'éclairage professionnel pour spectacles et événements",
    features: ["Éclairage LED", "Contrôle DMX", "Effets spéciaux"],
    image: "./images/IMG_1739.jpg"
  }, {
    icon: Shield,
    title: "Vidéo Surveillance",
    description: "Systèmes de sécurité avancés pour protéger vos biens",
    features: ["Caméras IP", "Enregistrement cloud", "Surveillance mobile"],
    image: "./images/tempImagecSHmJK.jpg"
  }, {
    icon: Satellite,
    title: "Installation Antennes Satellite",
    description: "Installation et configuration d'antennes paraboliques professionnelles",
    features: ["Antennes TV satellite", "Réception multi-satellites", "Configuration décodeurs"],
    image: "/images/IMG_1574.jpg"
  }, {
    icon: Wifi,
    title: "Télécommunications & Réseau",
    description: "Installation et maintenance de vos infrastructures de communication",
    features: ["Installation matériel Swisscom/Salt/Sunrise", "Dépannage fibre optique", "Optimisation réseau", "Diagnostic réseau"],
    image: "./images/ubiquiti_uap_ac_lr_punto_acceso_5_ghz_03_ad_l_edited.jpg"
  }];
  
  const advantages = ["Service client personnalisé et réactif", "Expertise technique reconnue", "Travail sur réseau sécurisé", "Solutions sur mesure", "Support technique", "Garantie sur tous nos services"];
  
  return <Layout>
      {/* Modal d'information importante */}
      <Dialog open={showImportantInfo} onOpenChange={setShowImportantInfo}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              Information Importante
            </DialogTitle>
            <DialogDescription className="text-left space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="font-medium text-green-800 flex items-center gap-2">
                  📞 <strong>Téléphones rétablis</strong>
                </p>
                <p className="text-green-700 text-sm">
                  Nos numéros de téléphone fixes refonctionne :
                </p>
                <div className="text-green-700 text-sm font-medium space-y-1">
                  <p>• <strong>026 660 45 88</strong></p>
                  <p>• <strong>026 662 02 50</strong></p>
                  <p>• <strong>024 552 13 13</strong></p>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="font-medium text-yellow-800 flex items-center gap-2">
                  📺 <strong>Changement de fréquences BIS TV</strong>
                </p>
                <p className="text-yellow-700 text-sm">
                  Les fréquences des programmes BIS TV ont changé. Si vous rencontrez des problèmes de réception, <strong>contactez-nous</strong> pour une mise à jour de votre installation.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowImportantInfo(false)}>
              J'ai compris
            </Button>
            <Button asChild>
              <Link to="/contact" onClick={() => setShowImportantInfo(false)}>
                Nous contacter
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-background to-muted py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Contenu texte */}
            <div className="space-y-8">
              <h1 className="text-4xl lg:text-6xl font-bold tracking-tight animate-fade-in-up">BT-Connect SARL</h1>
              <p className="text-xl lg:text-2xl text-muted-foreground animate-fade-in-up">
                Votre partenaire de confiance pour l'audiovisuel, la domotique, 
                les télécommunications et la sécurité en Suisse romande
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up">
                <Button size="lg" asChild>
                  <Link to="/contact">
                    Demander un devis <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/presentation">
                    Nos services
                  </Link>
                </Button>
              </div>
              
              {/* Avantages */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 animate-fade-in-up">
                {advantages.map((advantage, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{advantage}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Image */}
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/images/tempImagexeDwRc.jpg" 
                  alt="Installation audiovisuelle professionnelle BT-Connect" 
                  className="w-full h-full object-cover" 
                />
              </div>
              {/* Badge flottant */}
              <div className="absolute -bottom-4 -right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg">
                <span className="font-semibold">Installation Professionnelle</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Nos Services</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Des solutions complètes et personnalisées pour tous vos besoins en audiovisuel, 
              domotique, télécommunications et sécurité
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-full w-fit group-hover:bg-primary/20 transition-colors">
                    <service.icon className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl mb-2">{service.title}</CardTitle>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {service.image && (
                    <div className="mt-4 rounded-lg overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-32 object-cover"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold">
              Prêt à transformer votre espace ?
            </h2>
            <p className="text-xl opacity-90">
              Contactez-nous dès aujourd'hui pour une consultation gratuite et découvrez 
              comment nous pouvons améliorer votre environnement technologique.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/contact">
                  <Phone className="mr-2 h-4 w-4" />
                  Nous appeler
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary" asChild>
                <Link to="/contact">
                  <Mail className="mr-2 h-4 w-4" />
                  Nous écrire
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Bell className="h-6 w-6 text-primary" />
              <h3 className="text-2xl font-bold">Restez informé</h3>
            </div>
            <p className="text-muted-foreground">
              Recevez nos dernières actualités, conseils techniques et offres spéciales
            </p>
            <form onSubmit={handleNewsletterSubscription} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input 
                type="email" 
                placeholder="Votre adresse email" 
                className="flex-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubscribing}
                required
              />
              <Button type="submit" disabled={isSubscribing}>
                <Send className="mr-2 h-4 w-4" />
                {isSubscribing ? 'Inscription...' : "S'abonner"}
              </Button>
            </form>
            <p className="text-xs text-muted-foreground">
              Nous respectons votre vie privée. Désabonnement possible à tout moment.
            </p>
          </div>
        </div>
      </section>
    </Layout>;
};

export default Index;